import React from "react";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { erpApi } from "../../../api/erp";
import { ErpEmpty, ErpLoading, MovementStatusChip } from "./ErpUi";
import {
  ERP_BRAND,
  MOVEMENT_KIND_LABELS,
  PAYMENT_METHOD_LABELS,
  formatDateTime,
  formatMoney,
} from "../../../utils/erp";

function Section({ title, children }) {
  return (
    <Box sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${ERP_BRAND.border}`, bgcolor: "#fff" }}>
      <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, mb: 1.6 }}>{title}</Typography>
      {children}
    </Box>
  );
}

export default function MovementDetailDialog({ open, onClose, projectId, movementId }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open || !movementId) return;
    let mounted = true;
    setLoading(true);
    setError("");
    erpApi
      .getMovement(projectId, movementId)
      .then((response) => {
        if (mounted) setData(response);
      })
      .catch((err) => {
        if (mounted) setError(err.message || "No se pudo cargar el movimiento");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [open, projectId, movementId]);

  const movement = data?.movement;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
            {movement?.movementNumber || "Detalle del movimiento"}
          </Typography>
          {movement ? <Chip size="small" label={MOVEMENT_KIND_LABELS[movement.kind] || movement.kind} /> : null}
          {movement ? <MovementStatusChip status={movement.status} /> : null}
        </Stack>
        <IconButton onClick={onClose} sx={{ position: "absolute", top: 12, right: 12 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ bgcolor: "#FAFBFD" }}>
        {loading ? <ErpLoading label="Cargando trazabilidad..." /> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!loading && !error && movement ? (
          <Stack spacing={2.2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Section title="Resumen">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>DESCRIPCIÓN</Typography>
                      <Typography sx={{ fontWeight: 850, mt: 0.35 }}>{movement.description || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>FECHA</Typography>
                      <Typography sx={{ fontWeight: 850, mt: 0.35 }}>{formatDateTime(movement.occurredAt)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>REFERENCIA</Typography>
                      <Typography sx={{ fontWeight: 850, mt: 0.35 }}>{movement.sourceNumber || "—"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>TERCERO</Typography>
                      <Typography sx={{ fontWeight: 850, mt: 0.35 }}>{movement.party?.name || "Consumidor / no indicado"}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={2.5}>
                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>TOTAL</Typography>
                      <Typography sx={{ fontWeight: 950, mt: 0.35 }}>{formatMoney(movement.total, movement.currency)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={2.5}>
                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>PAGADO</Typography>
                      <Typography sx={{ fontWeight: 950, mt: 0.35 }}>{formatMoney(movement.paidAmount, movement.currency)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>PENDIENTE</Typography>
                      <Typography sx={{ fontWeight: 950, mt: 0.35 }}>{formatMoney(movement.balanceDue, movement.currency)}</Typography>
                    </Grid>
                  </Grid>
                </Section>
              </Grid>
              <Grid item xs={12} md={4}>
                <Section title="Aplicaciones">
                  <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
                    <Chip label={movement.stockApplied ? "Stock aplicado" : "Sin impacto de stock"} color={movement.stockApplied ? "success" : "default"} />
                    <Chip label={movement.ledgerApplied ? "Ledger aplicado" : "Sin asiento"} color={movement.ledgerApplied ? "success" : "default"} />
                    <Chip label={movement.cashApplied ? "Caja aplicada" : "Sin caja física"} color={movement.cashApplied ? "success" : "default"} />
                  </Stack>
                  {movement.notes ? <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 1.5 }}>{movement.notes}</Typography> : null}
                </Section>
              </Grid>
            </Grid>

            <Section title={`Ítems (${movement.items?.length || 0})`}>
              {!movement.items?.length ? <ErpEmpty title="Sin ítems" description="Este movimiento no contiene líneas de productos o servicios." /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Concepto</TableCell>
                        <TableCell align="right">Cantidad</TableCell>
                        <TableCell align="right">Precio / costo</TableCell>
                        <TableCell align="right">Variación stock</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {movement.items.map((item) => (
                        <TableRow key={item._id || `${item.nameSnapshot}-${item.quantity}`}>
                          <TableCell>
                            <Typography sx={{ fontWeight: 850 }}>{item.nameSnapshot}</Typography>
                            {item.variantLabelSnapshot ? (
                              <Typography variant="body2" sx={{ color: ERP_BRAND.orange, fontWeight: 800 }}>
                                {item.variantLabelSnapshot}{item.variantSkuSnapshot ? ` · SKU ${item.variantSkuSnapshot}` : ""}
                              </Typography>
                            ) : null}
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{item.codeSnapshot || item.categorySnapshot || ""}</Typography>
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{formatMoney(item.unitPrice || item.unitCost, movement.currency)}</TableCell>
                          <TableCell align="right">{item.stockDelta > 0 ? `+${item.stockDelta}` : item.stockDelta || "—"}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900 }}>{formatMoney(item.lineTotal, movement.currency)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>

            <Grid container spacing={2}>
              <Grid item xs={12} lg={6}>
                <Section title={`Pagos y transacciones (${data.transactions?.length || 0})`}>
                  {!data.transactions?.length ? <ErpEmpty title="Sin transacciones" description="No se registraron cobros o pagos para este movimiento." /> : (
                    <Stack spacing={1.2}>
                      {data.transactions.map((transaction) => (
                        <Stack key={transaction.id || transaction._id} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 1, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
                          <Box>
                            <Typography sx={{ fontWeight: 850 }}>{transaction.transactionNumber}</Typography>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{PAYMENT_METHOD_LABELS[transaction.method] || transaction.method} · {transaction.reference || "sin referencia"}</Typography>
                          </Box>
                          <Typography sx={{ fontWeight: 950 }}>{formatMoney(transaction.amount, transaction.currency)}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Section>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Section title={`Movimientos de caja (${data.cashMovements?.length || 0})`}>
                  {!data.cashMovements?.length ? <ErpEmpty title="Sin caja física" description="El movimiento no impactó una sesión de caja." /> : (
                    <Stack spacing={1.2}>
                      {data.cashMovements.map((cash) => (
                        <Stack key={cash.id || cash._id} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 1, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
                          <Box>
                            <Typography sx={{ fontWeight: 850 }}>{cash.cashMovementNumber}</Typography>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{cash.registerId?.name || "Caja"} · {cash.description}</Typography>
                          </Box>
                          <Typography sx={{ fontWeight: 950, color: cash.direction === "in" ? "success.main" : "error.main" }}>
                            {cash.direction === "in" ? "+" : "-"}{formatMoney(cash.amount, cash.currency)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Section>
              </Grid>
            </Grid>

            <Section title={`Asiento contable (${data.ledgerEntries?.length || 0} líneas)`}>
              {!data.ledgerEntries?.length ? <ErpEmpty title="Sin asiento" description="Este movimiento todavía no generó líneas contables." /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cuenta</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell align="right">Debe</TableCell>
                        <TableCell align="right">Haber</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.ledgerEntries.map((entry) => (
                        <TableRow key={entry.id || entry._id}>
                          <TableCell><strong>{entry.accountCode}</strong> · {entry.accountName}</TableCell>
                          <TableCell>{entry.description || "—"}</TableCell>
                          <TableCell align="right">{entry.side === "debit" ? formatMoney(entry.amount, entry.currency) : "—"}</TableCell>
                          <TableCell align="right">{entry.side === "credit" ? formatMoney(entry.amount, entry.currency) : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>

            <Section title={`Trazabilidad de stock (${data.stockMovements?.length || 0})`}>
              {!data.stockMovements?.length ? <ErpEmpty title="Sin impacto de stock" description="No hubo entradas, salidas ni ajustes asociados." /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Producto</TableCell>
                        <TableCell align="right">Anterior</TableCell>
                        <TableCell align="right">Variación</TableCell>
                        <TableCell align="right">Posterior</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.stockMovements.map((stock) => (
                        <TableRow key={stock.id || stock._id}>
                          <TableCell sx={{ fontWeight: 850 }}>{stock.productNameSnapshot}</TableCell>
                          <TableCell align="right">{stock.stockBefore}</TableCell>
                          <TableCell align="right" sx={{ color: stock.quantityDelta >= 0 ? "success.main" : "error.main", fontWeight: 900 }}>
                            {stock.quantityDelta > 0 ? `+${stock.quantityDelta}` : stock.quantityDelta}
                          </TableCell>
                          <TableCell align="right">{stock.stockAfter}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          </Stack>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
